import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { useFloating } from '@/shared/hooks/useFloating';

const fadeIn = keyframes`from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:translateY(0)}`;

const Wrap = styled.div`display:inline-flex;flex-direction:column;width:100%;`;

const Trigger = styled.button<{ $hasValue: boolean }>`
  display:flex;align-items:center;gap:8px;width:100%;height:34px;
  padding:0 10px;border-radius:7px;border:1px solid var(--border-subtle);
  background:var(--bg-primary);color:${({ $hasValue }) => ($hasValue ? 'var(--text-primary)' : 'var(--text-tertiary)')};
  font-size:13px;font-family:inherit;cursor:pointer;text-align:left;
  transition:border-color 0.12s ease;
  &:focus{outline:none;border-color:var(--accent);}
  &:hover{border-color:var(--accent);}
`;

const Chevron = styled.span<{ $open: boolean }>`
  margin-left:auto;display:flex;align-items:center;flex-shrink:0;
  transition:transform 0.12s ease;
  transform:${({ $open }) => ($open ? 'rotate(180deg)' : 'rotate(0)')};
`;

const Panel = styled.div<{ $x: number; $y: number; $w: number; $dir: 'up' | 'down' }>`
  position:absolute;z-index:9999;
  left:${({ $x }) => $x}px;
  top:${({ $dir, $y }) => $dir === 'down' ? `${$y + 4}px` : 'auto'};
  bottom:${({ $dir, $y }) => $dir === 'up' ? `${window.innerHeight - $y + 4}px` : 'auto'};
  width:${({ $w }) => Math.max($w, 180)}px;
  background:var(--bg-surface);border:1px solid var(--border-subtle);border-radius:10px;
  box-shadow:0 12px 40px rgba(0,0,0,0.22);padding:4px;
  animation:${fadeIn} 0.12s ease;max-height:280px;overflow:hidden;
  display:flex;flex-direction:column;
`;

const SearchInput = styled.input`
  height:32px;border-radius:6px;border:1px solid var(--border-subtle);
  background:var(--bg-primary);color:var(--text-primary);padding:0 10px;
  font-size:12px;font-family:inherit;margin:4px 4px 6px;flex-shrink:0;
  &:focus{outline:none;border-color:var(--accent);}
  &::placeholder{color:var(--text-tertiary);}
`;

const OptionsList = styled.div`overflow-y:auto;flex:1;`;

const Option = styled.button<{ $selected?: boolean; $highlighted?: boolean }>`
  display:flex;align-items:center;gap:8px;width:100%;
  padding:7px 10px;border-radius:6px;border:none;text-align:left;
  font-size:13px;font-family:inherit;cursor:pointer;
  transition:all 0.06s ease;
  background:${({ $selected, $highlighted }) => ($selected ? 'var(--accent-muted)' : $highlighted ? 'var(--bg-hover)' : 'transparent')};
  color:${({ $selected }) => ($selected ? 'var(--accent)' : 'var(--text-primary)')};
  font-weight:${({ $selected }) => ($selected ? 600 : 400)};
  &:hover{background:var(--bg-hover);}
`;

const OptionDot = styled.span<{ $c: string }>`width:8px;height:8px;border-radius:50%;background:${({ $c }) => $c};flex-shrink:0;`;

const EmptyMsg = styled.div`padding:16px;text-align:center;font-size:12px;color:var(--text-tertiary);`;

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" style={{ flexShrink: 0, marginLeft: 'auto' }}>
    <path d="M3 8l4 4 6-7" />
  </svg>
);

export type SelectOption = {
  value: string;
  label: string;
  color?: string;
  icon?: React.ReactNode;
};

type Props = {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  searchable?: boolean;
  clearable?: boolean;
  icon?: React.ReactNode;
};

export const CustomSelect: React.FC<Props> = ({ value, onChange, options, placeholder = 'Выберите...', searchable = false, clearable = false, icon }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const { anchorRef, pos } = useFloating(open, 300);
  const panelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const selected = useMemo(() => options.find((o) => o.value === value), [options, value]);

  const filtered = useMemo(() => {
    if (!query) return options;
    const q = query.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setHighlightIdx(-1);
    setTimeout(() => searchRef.current?.focus(), 50);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => {
      if (anchorRef.current?.contains(e.target as Node)) return;
      if (panelRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', h);
    document.addEventListener('keydown', k);
    return () => { document.removeEventListener('mousedown', h); document.removeEventListener('keydown', k); };
  }, [open, anchorRef]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlightIdx((i) => Math.min(i + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlightIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && highlightIdx >= 0 && highlightIdx < filtered.length) {
      e.preventDefault();
      onChange(filtered[highlightIdx].value);
      setOpen(false);
    }
  }, [filtered, highlightIdx, onChange]);

  useEffect(() => {
    if (highlightIdx < 0 || !listRef.current) return;
    const el = listRef.current.children[highlightIdx] as HTMLElement;
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [highlightIdx]);

  const select = useCallback((val: string) => {
    if (clearable && val === value) onChange('');
    else onChange(val);
    setOpen(false);
  }, [onChange, value, clearable]);

  const panel = open ? createPortal(
    <Panel ref={panelRef} $x={pos.left} $y={pos.top} $w={pos.width} $dir={pos.direction} onKeyDown={handleKeyDown}>
      {searchable && (
        <SearchInput
          ref={searchRef}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setHighlightIdx(0); }}
          placeholder="Поиск..."
        />
      )}
      <OptionsList ref={listRef}>
        {filtered.length === 0 && <EmptyMsg>Ничего не найдено</EmptyMsg>}
        {filtered.map((opt, i) => (
          <Option
            key={opt.value}
            type="button"
            $selected={opt.value === value}
            $highlighted={i === highlightIdx}
            onClick={() => select(opt.value)}
            onMouseEnter={() => setHighlightIdx(i)}
          >
            {opt.color && <OptionDot $c={opt.color} />}
            {opt.icon}
            {opt.label}
            {opt.value === value && <CheckIcon />}
          </Option>
        ))}
      </OptionsList>
    </Panel>,
    document.body,
  ) : null;

  return (
    <Wrap ref={anchorRef}>
      <Trigger type="button" $hasValue={!!selected} onClick={() => setOpen(!open)}>
        {icon}
        {selected?.color && <OptionDot $c={selected.color} />}
        {selected?.icon}
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selected?.label ?? placeholder}
        </span>
        <Chevron $open={open}>
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M4 6l4 4 4-4" /></svg>
        </Chevron>
      </Trigger>
      {panel}
    </Wrap>
  );
};
